import React from "react";
import { useNavigate } from "react-router-dom";
import { useListAllEvents } from "../api/events-api";
import { useLogeMembers } from "../api/user-api";
import { AdminRow, Card, Eyebrow } from "../components/dss/Primitives";
import { Icon } from "../components/dss/Icon";
import { Stagger, StaggerItem, useEnterAnimation } from "../components/dss/Motion";
import { Skeleton } from "../components/dss/Skeleton";

const EVENT_ICONS = ["fire", "house", "leaf", "calendar-days"] as const;

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: events = [], isLoading: isLoadingEvents } = useListAllEvents();
  const { data: members = [], isLoading: isLoadingMembers } = useLogeMembers();

  const animateIn = useEnterAnimation(isLoadingEvents);

  const openVotes = events.filter((e) => e.status === "draft" && e.show_vote_link).length;

  return (
    <div data-scroll-container data-sticky-scroll className="flex-1 overflow-y-auto px-7">
      <Eyebrow className="pt-2">[ Kun for admin ]</Eyebrow>
      <div className="font-display text-[46px] leading-[0.9] tracking-tight font-extrabold mt-1.5 uppercase">
        Styring<span className="text-bilberry">.</span>
      </div>

      <div className="mt-4.5 py-3.5 px-4 rounded-lg bg-ink text-paper flex gap-4">
        {[
          { v: String(events.length).padStart(2, "0"), k: "SAMLINGER" },
          { v: String(openVotes).padStart(2, "0"), k: "ÅBEN AFSTEMNING" },
          { v: String(members.length).padStart(2, "0"), k: "MEDLEMMER" },
        ].map((s, i) => (
          <React.Fragment key={s.k}>
            {i > 0 && <div className="w-px bg-white/18" />}
            <div className="flex-1">
              {isLoadingEvents || isLoadingMembers ? (
                <Skeleton className="w-8 h-[30px] bg-white/15" />
              ) : (
                <div className="font-display font-extrabold text-[30px] leading-none tracking-tight tabular-nums">
                  {s.v}
                </div>
              )}
              <div className="font-mono text-[8.5px] tracking-wide text-white/55 mt-1">{s.k}</div>
            </div>
          </React.Fragment>
        ))}
      </div>

      <div className="mt-5">
        <Eyebrow className="mb-2.5">[ Samlinger ]</Eyebrow>
        <Card>
          <button
            onClick={() => navigate("/event/add")}
            className="w-full flex items-center gap-2.5 py-3.5 px-3.5 bg-paper2"
          >
            <Icon name="plus" size={13} />
            <div className="flex-1 text-left font-display text-sm font-bold uppercase tracking-tight">
              Indkald ny samling
            </div>
            <Icon name="arrow-right" size={13} />
          </button>
          {isLoadingEvents ? (
            [0, 1].map((i) => (
              <div key={i} className="flex items-center gap-3 px-3.5 py-3.5 border-t border-line-soft">
                <Skeleton className="w-[30px] h-[30px] flex-shrink-0" />
                <div className="flex-1 flex flex-col gap-1.5">
                  <Skeleton className="w-2/3 h-4" />
                  <Skeleton className="w-1/3 h-3" />
                </div>
              </div>
            ))
          ) : (
            <Stagger enabled={animateIn}>
              {events.map((event, i) => (
                // AdminRow's own `first:border-t-0` now always matches (it's an
                // only-child of this wrapper), so the divider moves out here.
                <StaggerItem key={event.id} className="border-t border-line-soft">
                  <AdminRow
                    icon={EVENT_ICONS[i % EVENT_ICONS.length]}
                    label={event.title}
                    meta={
                      event.status === "confirmed"
                        ? "BEKRÆFTET"
                        : event.show_vote_link
                          ? "AFSTEMNING ÅBEN"
                          : "KLADDE"
                    }
                    tone={event.status === "confirmed" ? "text-sage" : "text-clay"}
                    onClick={() => navigate(`/edit/${event.id}`)}
                  />
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </Card>
      </div>

      <div className="mt-5 pb-6">
        <Eyebrow className="mb-2.5">[ Medlemmer & roller ]</Eyebrow>
        <Card>
          <AdminRow
            icon="user"
            label="Medlemsliste"
            meta={isLoadingMembers ? "…" : `${members.length} MEDLEMMER`}
            onClick={() => navigate("/admin/members")}
          />
          <AdminRow
            icon="user-plus"
            label="Tilføj medlem"
            meta="OPRETTER PLADS PÅ SIGNUP-LISTEN"
            onClick={() => navigate("/admin/members/add")}
          />
        </Card>
      </div>
    </div>
  );
};

export default AdminPage;
